import {
  bankNameOf,
  type GeneralMiss,
  isCdslCas,
  type ParsedCas,
  type PdfItems,
  PdfPasswordError,
  parseCdslCasLines,
  parseStatement as parseStatementXlsCsv,
  pdfTextLines,
  readPdf,
  readPdfStatement,
  statementBank,
} from "@paisa/parsers"
import type { ParsedStatement } from "@paisa/types"

export class PasswordRequiredError extends Error {
  readonly kind: "required" | "incorrect"
  constructor(kind: "required" | "incorrect") {
    super(kind === "required" ? "PDF password required" : "PDF password incorrect")
    this.kind = kind
  }
}

/**
 * No parser reads the file. For a PDF, `reason` is the general reader's (no-table, math, scanned) and `bank`
 * the bank code its header names (for the "can't read <bank> yet" screen and the per-bank count).
 */
export class UnsupportedFormatError extends Error {
  readonly reason?: GeneralMiss
  readonly bank?: string
  constructor(reason?: GeneralMiss, bank?: string) {
    super("File format not recognised — no matching parser")
    this.reason = reason
    this.bank = bank
  }
}

/** The miss screen's copy: null = the plain "couldn't recognise this file" message (not a statement PDF). */
export function missCopy(err: UnsupportedFormatError): { title: string; body: string } | null {
  if (!err.reason || err.reason === "password") return null
  const name = err.bank ? bankNameOf(err.bank) : null
  return {
    title: name
      ? `We can't read ${name} statements yet.`
      : "We can't read this bank's statements yet.",
    body:
      err.reason === "math"
        ? "The rows didn't add up to the statement's totals, so nothing was imported."
        : err.reason === "scanned"
          ? "This PDF is a scanned image, so there's no text to read. Download it again from your bank's site or app."
          : "We add the banks people ask for.",
  }
}

/** Rows as an event value, bucketed (never the count itself): the phone's buckets. */
const bucket = (n: number) => (n === 0 ? "0" : n <= 50 ? "1-50" : n <= 200 ? "51-200" : "200+")

/**
 * The `import_statement` event for the per-bank hit/miss count (same props as the phone's): the bank code and
 * the outcome. Only short codes: never rows, amounts, names or narration. A miss is counted when the read
 * fails; a read when it is imported (the preview can't edit a row on the web, so an import is a hit).
 */
export function missEvent(err: UnsupportedFormatError): Record<string, string> {
  return {
    result: err.reason ? "pdf" : "unknown",
    rows: "0",
    offline: "false",
    bank: err.bank || "unknown",
    outcome: `miss-${err.reason ?? "unknown"}`,
  }
}

export function importEvent(parsed: ParsedStatement, imported: number): Record<string, string> {
  return {
    result: "imported",
    rows: bucket(imported),
    offline: "false",
    source: parsed.bank || "unknown",
    bank: parsed.bank || "unknown",
    outcome: "hit",
  }
}

/** The file is a CDSL CAS (holdings, not transactions): use parseCasFile. */
export class CasDetectedError extends Error {
  constructor() {
    super("This is a CDSL consolidated account statement")
  }
}

/** Returns true if the PDF has an /Encrypt dictionary (password-protected). */
function isEncryptedPdf(data: ArrayBuffer): boolean {
  const text = new TextDecoder("latin1").decode(data.slice(0, 4096))
  return text.includes("/Encrypt")
}

/** Returns true if the file's magic bytes indicate a PDF. */
function isPdfBuffer(data: ArrayBuffer): boolean {
  const head = new Uint8Array(data.slice(0, 5))
  return (
    head[0] === 0x25 && // %
    head[1] === 0x50 && // P
    head[2] === 0x44 && // D
    head[3] === 0x46 && // F
    head[4] === 0x2d // -
  )
}

/** The PDF's text items; a password PDF becomes PasswordRequiredError for the caller to prompt. */
async function readItems(data: ArrayBuffer, password?: string): Promise<PdfItems> {
  try {
    return await readPdf(new Uint8Array(data), { password })
  } catch (err) {
    if (err instanceof PdfPasswordError) {
      throw new PasswordRequiredError(err.wrong ? "incorrect" : "required")
    }
    throw err
  }
}

/** Parse a CDSL CAS on the device (same parser as MCP import_statement from='cas'). */
export async function parseCasFile(data: ArrayBuffer, password?: string): Promise<ParsedCas> {
  return parseCdslCasLines(pdfTextLines(await readItems(data, password)))
}

/**
 * Parse any supported bank statement (PDF / XLS / CSV). Runs fully in the
 * browser — file contents never leave the device.
 *
 * Throws PasswordRequiredError for encrypted PDFs (caller prompts for it).
 * Throws UnsupportedFormatError if no parser matches.
 */
export async function parseStatementFile(
  data: ArrayBuffer,
  password?: string,
): Promise<ParsedStatement> {
  if (isPdfBuffer(data)) {
    if (!password && isEncryptedPdf(data)) {
      throw new PasswordRequiredError("required")
    }
    const items = await readItems(data, password)
    const rawText = items
      .filter((i) => i.page <= 2)
      .map((i) => i.str)
      .join(" ")
    if (isCdslCas(rawText)) throw new CasDetectedError()
    // The bank routes (yearly before monthly, and so on), then the general reader, live in readPdfStatement
    const read = readPdfStatement(items)
    const bank = statementBank(items)?.code
    if (!read.ok || !read.statement.transactions.length)
      throw new UnsupportedFormatError(read.ok ? "no-table" : read.reason, bank)
    // the general reader names no bank: the header's
    return read.statement.bank ? read.statement : { ...read.statement, bank: bank ?? "" }
  }

  const result = parseStatementXlsCsv(data)
  if (!result) throw new UnsupportedFormatError()
  return result
}
